import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { AuthResponse, ApiError } from '@/types';

// Create axios instance
// Increased timeout for remote database (Supabase) connections
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1',
  timeout: 30000,  // 30 seconds for remote database
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to decode JWT token and get expiration
const getTokenExpiration = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch (error) {
    return null;
  }
};

// Helper function to check if token is about to expire (within 5 minutes)
const isTokenExpiringSoon = (token: string): boolean => {
  const expiration = getTokenExpiration(token);
  if (!expiration) return false;
  
  const now = Date.now();
  const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
  return expiration - now < fiveMinutes;
};

// Helper function to refresh token proactively
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let isRedirecting = false; // Flag to prevent multiple redirects

// Reset redirecting flag after a delay to prevent stuck state
const resetRedirectingFlag = () => {
  setTimeout(() => {
    isRedirecting = false;
  }, 5000); // Reset after 5 seconds
};

const refreshTokenProactively = async (): Promise<string> => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        throw new Error('No token available');
      }

      // Use the current access token to refresh (backend accepts it)
      const response = await axios.post(
        `${api.defaults.baseURL}/auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      return access_token;
    } catch (error) {
      // If refresh fails, clear tokens and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Request interceptor to add auth token and refresh if needed
api.interceptors.request.use(
  async (config) => {
    // Reset redirecting flag if it's been stuck (safety check)
    if (isRedirecting && window.location.pathname !== '/auth/login' && window.location.pathname !== '/auth/signup') {
      console.warn('Redirecting flag was stuck, resetting...');
      isRedirecting = false;
    }
    
    const token = localStorage.getItem('token');
    
    if (token) {
      // Check if token is about to expire and refresh proactively
      if (isTokenExpiringSoon(token)) {
        try {
          const newToken = await refreshTokenProactively();
          config.headers.Authorization = `Bearer ${newToken}`;
        } catch (error) {
          // If refresh fails, the request will fail with 401 and be handled by response interceptor
          config.headers.Authorization = `Bearer ${token}`;
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // If we're already redirecting, reject immediately to prevent hanging
      if (isRedirecting) {
        return Promise.reject(new Error('Authentication required - redirecting to login'));
      }

      // Mark that we're redirecting to prevent multiple redirects
      isRedirecting = true;
      resetRedirectingFlag(); // Auto-reset after delay

      // Clear token immediately to prevent further API calls with stale token
      const currentToken = localStorage.getItem('token');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      // Helper function to redirect to login
      const redirectToLogin = () => {
        // Store the current URL to redirect back after login
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== '/auth/login' && currentPath !== '/auth/signup') {
          sessionStorage.setItem('redirectAfterLogin', currentPath);
        }
        
        // Store the pending action (request details) so we can retry after login
        // Only store POST, PUT, PATCH, DELETE actions (not GET requests)
        if (originalRequest && originalRequest.url) {
          const method = originalRequest.method?.toUpperCase() || 'GET';
          // Only store actions that modify data (not GET requests)
          if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
            // Get the relative URL (remove base URL if present)
            let url = originalRequest.url;
            const baseURL = api.defaults.baseURL || '';
            if (url.startsWith(baseURL)) {
              url = url.replace(baseURL, '');
            }
            // Ensure it starts with /
            if (!url.startsWith('/')) {
              url = `/${url}`;
            }
            
            // Parse data if it's a string (JSON)
            let requestData = originalRequest.data;
            if (typeof requestData === 'string') {
              try {
                requestData = JSON.parse(requestData);
              } catch (e) {
                // Keep as string if parsing fails
              }
            }
            
            const pendingAction = {
              method: method,
              url: url,
              data: requestData,
              params: originalRequest.params,
              timestamp: Date.now(),
            };
            
            console.log('Storing pending action:', pendingAction);
            sessionStorage.setItem('pendingAction', JSON.stringify(pendingAction));
          }
        }
        
        // Redirect to login immediately (use setTimeout to allow promise rejection first)
        setTimeout(() => {
          window.location.href = '/auth/login';
        }, 100);
      };
      
      // If no token exists or token refresh is not needed, redirect immediately
      if (!currentToken || !originalRequest._retry) {
        redirectToLogin();
        // Reject immediately to prevent timeout - the redirect will happen
        return Promise.reject(new Error('Authentication required'));
      }

      // If token exists and we haven't retried, try to refresh it
      if (originalRequest._retry === undefined) {
        originalRequest._retry = true;
        
        try {
          const response = await axios.post(
            `${api.defaults.baseURL}/auth/refresh`,
            {},
            {
              headers: {
                Authorization: `Bearer ${currentToken}`,
              },
            }
          );

          const { access_token } = response.data;
          localStorage.setItem('token', access_token);
          isRedirecting = false; // Reset flag on success

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          redirectToLogin();
          // Reject immediately to prevent timeout
          return Promise.reject(new Error('Token refresh failed - redirecting to login'));
        }
      } else {
        // Already retried, just redirect
        redirectToLogin();
        // Reject immediately to prevent timeout
        return Promise.reject(new Error('Session expired - redirecting to login'));
      }
    }

    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      const timeoutError: ApiError = {
        detail: 'Request timed out. Please check your connection and try again.',
        status_code: 408,
        request_id: error.response?.data?.request_id,
      };
      return Promise.reject(timeoutError);
    }

    // Format error response
    // Skip formatting if we're redirecting (prevents error from being caught and shown)
    if (isRedirecting) {
      // Reject with a special error that won't show toast
      return Promise.reject(new Error('Redirecting to login'));
    }

    let errorDetail: string;
    const responseDetail = error.response?.data?.detail;
    
    // Check if this is an authentication-related error message
    const isAuthError = typeof responseDetail === 'string' && (
      responseDetail.toLowerCase().includes('not authenticated') ||
      responseDetail.toLowerCase().includes('authentication') ||
      responseDetail.toLowerCase().includes('unauthorized') ||
      responseDetail.toLowerCase().includes('token') ||
      responseDetail.toLowerCase().includes('login')
    );

    // If it's an auth error but not a 401 (shouldn't happen, but just in case)
    if (isAuthError && error.response?.status !== 401) {
      // Still redirect to be safe
      isRedirecting = true;
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== '/auth/login' && currentPath !== '/auth/signup') {
        sessionStorage.setItem('redirectAfterLogin', currentPath);
      }
      window.location.href = '/auth/login';
      return new Promise(() => {});
    }
    
    if (Array.isArray(responseDetail)) {
      // Handle FastAPI validation errors (422) - array of error objects
      errorDetail = responseDetail
        .map((err: any) => {
          if (typeof err === 'string') return err;
          if (err.msg) return `${err.loc?.join('.') || 'field'}: ${err.msg}`;
          if (err.message) return err.message;
          return JSON.stringify(err);
        })
        .join(', ');
    } else if (typeof responseDetail === 'string') {
      errorDetail = responseDetail;
    } else if (responseDetail && typeof responseDetail === 'object') {
      // If detail is an object but not an array, stringify it
      errorDetail = JSON.stringify(responseDetail);
    } else {
      errorDetail = error.message || 'An error occurred';
    }
    
    const apiError: ApiError = {
      detail: errorDetail,
      status_code: error.response?.status || 500,
      request_id: error.response?.data?.request_id,
    };

    return Promise.reject(apiError);
  }
);

// Generic API methods
export const apiClient = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.get(url, config),
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.post(url, data, config),
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.put(url, data, config),
  
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.patch(url, data, config),
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    api.delete(url, config),
};

// Auth API
export const authApi = {
  login: (credentials: { email: string; password: string }) =>
    apiClient.post<AuthResponse>('/auth/login', credentials),
  
  signup: (userData: any) =>
    apiClient.post('/auth/signup', userData),
  
  me: () =>
    apiClient.get('/auth/me'),
  
  refresh: () =>
    apiClient.post<AuthResponse>('/auth/refresh'),
  
  changePassword: (passwordData: { current_password: string; new_password: string }) =>
    apiClient.post('/auth/change-password', passwordData),
  
  logout: () =>
    apiClient.post('/auth/logout'),
};

// Products API
export const productsApi = {
  getAll: (params?: any) =>
    apiClient.get('/products/', { params }),
  
  search: (query: string, params?: any) =>
    apiClient.get(`/products/search?q=${encodeURIComponent(query)}`, { params }),
  
  getFeatured: (params?: { skip?: number; limit?: number }) =>
    apiClient.get('/products/featured', { params }),
  
  getByType: (type: 'brand_new' | 'recycled', params?: { skip?: number; limit?: number }) =>
    apiClient.get(`/products/type/${type}`, { params }),
  
  getById: (id: number) =>
    apiClient.get(`/products/${id}`),
  
  create: (productData: any) =>
    apiClient.post('/products/', productData),
  
  createWithImages: (formData: FormData) =>
    apiClient.post('/products/with-images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  update: (id: number, productData: any) =>
    apiClient.put(`/products/${id}/update`, productData, {
      headers: productData instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : undefined
    }),

  createProductWithImages: (formData: FormData) =>
    apiClient.post('/products/with-images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  
  updateImages: (id: number, formData: FormData) =>
    apiClient.put(`/products/${id}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  
  updateVisibility: (id: number, isPublic: boolean) =>
    apiClient.patch(`/products/${id}/visibility?is_public=${isPublic}`),
  
  toggleFeature: (id: number, isFeatured: boolean) =>
    apiClient.patch(`/products/${id}/feature?is_featured=${isFeatured}`),
  
  getMyProducts: (params?: { skip?: number; limit?: number; include_private?: boolean }) =>
    apiClient.get('/products/my-products', { params }),
  
  delete: (id: number) =>
    apiClient.delete(`/products/${id}`),
  
  // New comprehensive filter endpoint
  filterProducts: (filters: {
    category_ids?: number[];
    category_slugs?: string[];
    product_types?: string[];
    brands?: string[];
    conditions?: string[];
    locations?: string[];
    latitude?: number;
    longitude?: number;
    radius_km?: number;
    min_price?: number;
    max_price?: number;
    search_query?: string;
    skip?: number;
    limit?: number;
  }) => {
    const params: any = {
      skip: filters.skip || 0,
      limit: filters.limit || 20,
    };

    if (filters.category_ids && filters.category_ids.length > 0) {
      params.category_ids = filters.category_ids.join(',');
    }

    if (filters.category_slugs && filters.category_slugs.length > 0) {
      params.category_slugs = filters.category_slugs.join(',');
    }

    if (filters.product_types && filters.product_types.length > 0) {
      params.product_types = filters.product_types.join(',');
    }

    if (filters.brands && filters.brands.length > 0) {
      params.brands = filters.brands.join(',');
    }

    if (filters.conditions && filters.conditions.length > 0) {
      params.conditions = filters.conditions.join(',');
    }

    if (filters.locations && filters.locations.length > 0) {
      params.locations = filters.locations.join(',');
    }

    if (filters.latitude !== undefined) {
      params.latitude = filters.latitude;
    }

    if (filters.longitude !== undefined) {
      params.longitude = filters.longitude;
    }

    if (filters.radius_km !== undefined) {
      params.radius_km = filters.radius_km;
    }

    if (filters.min_price !== undefined) {
      params.min_price = filters.min_price;
    }

    if (filters.max_price !== undefined) {
      params.max_price = filters.max_price;
    }

    if (filters.search_query) {
      params.search_query = filters.search_query;
    }

    // Increase timeout for filter requests as they might take longer
    return apiClient.get('/products/filter', {
      params,
      timeout: 30000 // 30 seconds for filter requests
    });
  },
  
  // Filter Options
  getBrands: () =>
    apiClient.get('/products/filter-options/brands'),

  getConditions: () =>
    apiClient.get('/products/filter-options/conditions'),

  getCategories: () =>
    apiClient.get('/products/filter-options/categories'),

  getProductTypes: () =>
    apiClient.get('/products/filter-options/product-types'),

  getLocations: () =>
    apiClient.get('/products/filter-options/locations'),

  getPriceRange: () =>
    apiClient.get('/products/filter-options/price-range'),
};

// Images API - New image management system
export const imagesApi = {
  // Upload image(s) for a product
  upload: (productId: number, files: File[], isPrimary: boolean = false, displayOrder: number = 0) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('file', file);
    });
    if (isPrimary) formData.append('is_primary', 'true');
    if (displayOrder > 0) formData.append('display_order', displayOrder.toString());
    
    return apiClient.post(`/images/upload/${productId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Get image by ID (returns binary data)
  getById: (imageId: number) =>
    apiClient.get(`/images/${imageId}`, {
      responseType: 'blob',
    }),

  // Get all images for a product
  getByProduct: (productId: number) =>
    apiClient.get(`/images/product/${productId}`),

  // Delete an image
  delete: (imageId: number) =>
    apiClient.delete(`/images/${imageId}`),

  // Set image as primary
  setPrimary: (imageId: number) =>
    apiClient.put(`/images/${imageId}/primary`),

  // Get cache statistics (admin only)
  getCacheStats: () =>
    apiClient.get('/images/cache/stats'),

  // Warm cache for products (admin only)
  warmCache: (productIds: number[]) =>
    apiClient.post('/images/cache/warm', { product_ids: productIds }),
};

// Categories API
export const categoriesApi = {
  getAll: (includeAll?: boolean) => {
    const params: any = {};
    if (includeAll) params.include_all = true;
    return apiClient.get('/categories/', { params });
  },

  getByParent: (parentId?: number) => {
    const params: any = {};
    if (parentId !== undefined) params.parent_id = parentId;
    return apiClient.get('/categories/', { params });
  },

  getBySlug: (slug: string) =>
    apiClient.get(`/categories/slug/${slug}`),

  getTree: () =>
    apiClient.get('/categories/tree'),

  getCategoryPath: (categoryId: number) =>
    apiClient.get(`/categories/${categoryId}/path`),

  create: (categoryData: any) =>
    apiClient.post('/categories/', categoryData),

  update: (id: number, categoryData: any) =>
    apiClient.put(`/categories/${id}`, categoryData),

  delete: (id: number) =>
    apiClient.delete(`/categories/${id}`),

  uploadImage: (categoryId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/categories/${categoryId}/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  deleteImage: (categoryId: number) =>
    apiClient.delete(`/categories/${categoryId}/image`),
};

// Shops API
// Homepage API
export const homepageApi = {
  getContent: () =>
    apiClient.get('/homepage/content'),
  
  // Banners
  getBanners: () =>
    apiClient.get('/homepage/banners'),
  createBanner: (data: any) =>
    apiClient.post('/homepage/banners', data),
  updateBanner: (id: number, data: any) =>
    apiClient.put(`/homepage/banners/${id}`, data),
  deleteBanner: (id: number) =>
    apiClient.delete(`/homepage/banners/${id}`),
  uploadBannerImage: (bannerId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/homepage/banners/${bannerId}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteBannerImage: (bannerId: number) =>
    apiClient.delete(`/homepage/banners/${bannerId}/image`),
  
  // Sections
  getSections: () =>
    apiClient.get('/homepage/sections'),
  createSection: (data: any) =>
    apiClient.post('/homepage/sections', data),
  updateSection: (id: number, data: any) =>
    apiClient.put(`/homepage/sections/${id}`, data),
  deleteSection: (id: number) =>
    apiClient.delete(`/homepage/sections/${id}`),
  
  // Category Groups
  getCategoryGroups: () =>
    apiClient.get('/homepage/category-groups'),
  createCategoryGroup: (data: any) =>
    apiClient.post('/homepage/category-groups', data),
  updateCategoryGroup: (id: number, data: any) =>
    apiClient.put(`/homepage/category-groups/${id}`, data),
  deleteCategoryGroup: (id: number) =>
    apiClient.delete(`/homepage/category-groups/${id}`),
  
  // Promo Cards
  getPromoCards: () =>
    apiClient.get('/homepage/promo-cards'),
  createPromoCard: (data: any) =>
    apiClient.post('/homepage/promo-cards', data),
  updatePromoCard: (id: number, data: any) =>
    apiClient.put(`/homepage/promo-cards/${id}`, data),
  deletePromoCard: (id: number) =>
    apiClient.delete(`/homepage/promo-cards/${id}`),
  uploadPromoImage: (cardId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/homepage/promo-cards/${cardId}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deletePromoImage: (cardId: number) =>
    apiClient.delete(`/homepage/promo-cards/${cardId}/image`),
};

export const shopsApi = {
  getAll: () =>
    apiClient.get('/shops/'),
  
  getMyShop: () =>
    apiClient.get('/shops/my-shop'),
  
  getMyShopLink: () =>
    apiClient.get('/shops/my-shop/link'),
  
  getById: (id: number) =>
    apiClient.get(`/shops/${id}`),
  
  getPublicBySlug: (slug: string, params?: { skip?: number; limit?: number }) =>
    apiClient.get(`/shops/public/slug/${slug}`, { params }),
  
  getPublicById: (id: number, params?: { skip?: number; limit?: number }) =>
    apiClient.get(`/shops/public/${id}`, { params }),
  
  getProductsByShopId: (shopId: number, params?: { skip?: number; limit?: number }) =>
    apiClient.get(`/shops/${shopId}/products`, { params }),
  
  getProductsBySlug: (slug: string, params?: { skip?: number; limit?: number }) =>
    apiClient.get(`/shops/slug/${slug}/products`, { params }),
  
  create: (shopData: any) =>
    apiClient.post('/shops/', shopData),
  
  update: (id: number, shopData: any) =>
    apiClient.put(`/shops/${id}`, shopData),
  
  updateVisibility: (id: number, isPublic: boolean) =>
    apiClient.patch(`/shops/${id}/visibility?is_public=${isPublic}`),
  
  rateShop: (id: number, rating: number) =>
    apiClient.patch(`/shops/${id}/admin-rating?admin_rating=${rating}`),
};

// Cart API
export const cartApi = {
  get: () =>
    apiClient.get('/cart/'),

  addItem: (itemData: { product_id: number; quantity: number; selected_delivery_option_id?: number }) =>
    apiClient.post('/cart/add-item', itemData),

  getItems: () =>
    apiClient.get('/cart/items'),

  updateItem: (itemId: number, quantity: number) =>
    apiClient.put(`/cart/items/${itemId}`, { quantity }),

  updateDeliveryOption: (itemId: number, deliveryOptionId: number) =>
    apiClient.put(`/cart/items/${itemId}/delivery-option`, { selected_delivery_option_id: deliveryOptionId }),

  increaseItem: (itemId: number) =>
    apiClient.post(`/cart/items/${itemId}/increase`),

  decreaseItem: (itemId: number) =>
    apiClient.post(`/cart/items/${itemId}/decrease`),

  removeItem: (itemId: number) =>
    apiClient.delete(`/cart/items/${itemId}`),

  getTotal: () =>
    apiClient.get('/cart/total'),

  clear: () =>
    apiClient.delete('/cart/clear'),

  saveForLater: (itemId: number) =>
    apiClient.post(`/cart/items/${itemId}/save-for-later`),

  moveToCart: (itemId: number) =>
    apiClient.post(`/cart/items/${itemId}/move-to-cart`),

  getSavedForLater: () =>
    apiClient.get('/cart/saved-for-later'),

  checkout: () =>
    apiClient.post('/cart/checkout'),
};

// System Settings API
export const systemSettingsApi = {
  getFreeMode: () =>
    apiClient.get('/admin/system-settings/public/free-mode'),
  getSystemSettings: () =>
    apiClient.get('/admin/system-settings'),
  getSystemSetting: (key: string) =>
    apiClient.get(`/admin/system-settings/${key}`),
  updateSystemSetting: (key: string, value: string, description?: string, is_active?: boolean) => {
    const params: any = { value };
    if (description !== undefined) params.description = description;
    if (is_active !== undefined) params.is_active = is_active;
    return apiClient.put(`/admin/system-settings/${key}`, null, { params });
  },
};

// Orders API
export const ordersApi = {
  getAll: () =>
    apiClient.get('/orders/'),
  
  getById: (id: number) =>
    apiClient.get(`/orders/${id}`),
  
  getByOrderNumber: (orderNumber: string) =>
    apiClient.get(`/orders/by-number/${orderNumber}`),
  
  createFromCart: (orderData: any) =>
    apiClient.post('/orders/from-cart', null, { params: orderData }),
  
  update: (id: number, orderData: any) =>
    apiClient.put(`/orders/${id}`, orderData),
  
  cancel: (id: number) =>
    apiClient.delete(`/orders/${id}`),
  
  getItems: (id: number) =>
    apiClient.get(`/orders/${id}/items`),
  
  getTotal: (id: number) =>
    apiClient.get(`/orders/${id}/total`),
  
  getSellerOrders: () =>
    apiClient.get('/orders/seller/orders'),
  
  updateStatus: (id: number, newStatus: string) =>
    apiClient.post(`/orders/seller/${id}/update-status`, null, { params: { new_status: newStatus } }),
  
  adminUpdateStatus: (id: number, newStatus: string) =>
    apiClient.post(`/orders/admin/${id}/update-status`, null, { params: { new_status: newStatus } }),
  
  payCommission: (id: number, paymentMethod: string) =>
    apiClient.post(`/orders/seller/${id}/pay-commission?payment_method=${paymentMethod}`),
  
  getBuyerContact: (id: number) =>
    apiClient.get(`/orders/seller/${id}/buyer-contact`),
  
  confirmDelivery: (id: number) =>
    apiClient.post(`/orders/${id}/confirm-delivery`),

  // Seller Analytics
  getSellerDashboard: () =>
    apiClient.get('/orders/seller/dashboard'),
  
  getSellerAnalytics: (startDate?: string, endDate?: string) =>
    apiClient.get('/orders/seller/analytics', {
      params: { start_date: startDate, end_date: endDate }
    }),
  
  getSellerRecentOrders: (limit: number = 10) =>
    apiClient.get('/orders/seller/recent-orders', {
      params: { limit }
    }),
};

// Inventory API
export const inventoryApi = {
  getProductInventory: (productId: number) =>
    apiClient.get(`/inventory/product/${productId}`),
  
  getMyInventory: () =>
    apiClient.get('/inventory/my-inventory'),
  
  getProductsWithoutInventory: () =>
    apiClient.get('/inventory/products-without-inventory'),
  
  bulkCreate: (productIds: number[]) =>
    apiClient.post('/inventory/bulk-create', productIds),
  
  create: (productId: number, inventoryData: any) =>
    apiClient.post(`/inventory/?product_id=${productId}`, inventoryData),
  
  update: (id: number, inventoryData: any) =>
    apiClient.put(`/inventory/${id}`, inventoryData),
  
  addStock: (id: number, quantity: number) =>
    apiClient.post(`/inventory/${id}/add-stock?quantity=${quantity}`),
  
  removeStock: (id: number, quantity: number) =>
    apiClient.post(`/inventory/${id}/remove-stock?quantity=${quantity}`),
  
  setStock: (id: number, quantity: number) =>
    apiClient.post(`/inventory/${id}/set-stock?quantity=${quantity}`),
  
  getLowStock: (threshold?: number) =>
    apiClient.get(`/inventory/low-stock?threshold=${threshold || 10}`),
  
  getOutOfStock: () =>
    apiClient.get('/inventory/out-of-stock'),
  
  getAnalytics: () =>
    apiClient.get('/inventory/analytics'),
};

// Reviews API
export const reviewsApi = {
  getAll: (productId?: number) =>
    apiClient.get(`/reviews/?product_id=${productId || ''}`),
  
  getProductStats: (productId: number) =>
    apiClient.get(`/reviews/product/${productId}/stats`),
  
  getMyReviews: () =>
    apiClient.get('/reviews/my-reviews'),
  
  create: (reviewData: any) =>
    apiClient.post('/reviews/', reviewData),
  
  getByRating: (rating: number, productId?: number) =>
    apiClient.get(`/reviews/rating/${rating}?product_id=${productId || ''}`),
  
  getVerifiedPurchases: (productId: number) =>
    apiClient.get(`/reviews/verified-purchases?product_id=${productId}`),
  
  getById: (id: number) =>
    apiClient.get(`/reviews/${id}`),
  
  update: (id: number, reviewData: any) =>
    apiClient.put(`/reviews/${id}`, reviewData),
  
  delete: (id: number) =>
    apiClient.delete(`/reviews/${id}`),
  
  getSellerProductReviews: (productId: number) =>
    apiClient.get(`/reviews/seller/product-reviews?product_id=${productId}`),
};

// Wishlist API
export const wishlistApi = {
  getMyWishlist: (skip?: number, limit?: number) =>
    apiClient.get('/wishlist/', { params: { skip, limit } }),
  
  addToWishlist: (productId: number) =>
    apiClient.post('/wishlist/', { product_id: productId }),
  
  removeFromWishlist: (productId: number) =>
    apiClient.delete(`/wishlist/${productId}`),
  
  removeWishlistItem: (wishlistId: number) =>
    apiClient.delete(`/wishlist/item/${wishlistId}`),
  
  checkProductInWishlist: (productId: number) =>
    apiClient.get(`/wishlist/check/${productId}`),
};
// Brands API
export const brandsApi = {
  getAll: (activeOnly: boolean = true) =>
    apiClient.get('/brands/', { params: { active_only: activeOnly } }),
  
  search: (searchTerm: string) =>
    apiClient.get('/brands/search', { params: { search_term: searchTerm } }),
  
  getById: (id: number) =>
    apiClient.get(`/brands/${id}`),
  
  create: (brandData: any) =>
    apiClient.post('/brands/', brandData),
  
  update: (id: number, brandData: any) =>
    apiClient.put(`/brands/${id}`, brandData),
  
  delete: (id: number) =>
    apiClient.delete(`/brands/${id}`),
};

// Product Field Configuration API
export const productFieldConfigApi = {
  getAll: (activeOnly: boolean = true, visibleOnly: boolean = false) =>
    apiClient.get('/product-field-configs/', { params: { active_only: activeOnly, visible_only: visibleOnly } }),

  getForProductCreation: (categoryId: number) =>
    apiClient.get(`/product-field-configs/for-product-creation?category_id=${categoryId}`),

  getCategoriesForDropdown: () =>
    apiClient.get('/product-field-configs/categories-for-dropdown'),

  getById: (id: number) =>
    apiClient.get(`/product-field-configs/${id}`),

  create: (fieldData: any) =>
    apiClient.post('/product-field-configs/', fieldData),

  update: (id: number, fieldData: any) =>
    apiClient.put(`/product-field-configs/${id}`, fieldData),

  delete: (id: number) =>
    apiClient.delete(`/product-field-configs/${id}`),
};

// Users API
export const usersApi = {
  getAll: () =>
    apiClient.get('/users/'),
  
  getMe: () =>
    apiClient.get('/users/me'),
  
  getById: (id: number) =>
    apiClient.get(`/users/${id}`),
  
  update: (id: number, userData: any) =>
    apiClient.put(`/users/${id}`, userData),
  
  activate: (id: number) =>
    apiClient.post(`/users/${id}/activate`),
  
  deactivate: (id: number) =>
    apiClient.post(`/users/${id}/deactivate`),
  
  checkSellerEligibility: () =>
    apiClient.get('/users/me/seller-eligibility'),
};

// Admin API
export const adminApi = {
  getSuperAdminDashboard: () =>
    apiClient.get('/admin/super-admin/dashboard'),
  
  getAdminDashboard: () =>
    apiClient.get('/admin/admin/dashboard'),
  
  getStaffDashboard: () =>
    apiClient.get('/admin/staff/dashboard'),
  
  getAllUsers: () =>
    apiClient.get('/admin/admin/users'),
  
  activateUser: (id: number) =>
    apiClient.post(`/users/${id}/activate`),
  
  deactivateUser: (id: number) =>
    apiClient.post(`/users/${id}/deactivate`),
  
  getAllShops: () =>
    apiClient.get('/admin/admin/shops'),
  
  getAllCategories: () =>
    apiClient.get('/categories/'),
  
  updateShopPriority: (shopId: number, data: {
    priority_rank?: 'premium' | 'general' | 'basic';
    priority_score?: number;
    is_premium?: boolean;
  }) =>
    apiClient.patch(`/admin/admin/shops/${shopId}/priority`, data),
  
  rateShop: (shopId: number, rating: number) =>
    apiClient.post(`/admin/admin/shops/${shopId}/rate?rating=${rating}`),
  
  approveShop: (shopId: number) =>
    apiClient.patch(`/shops/${shopId}/visibility?is_public=true`),
  
  rejectShop: (shopId: number, reason: string) =>
    apiClient.patch(`/shops/${shopId}/visibility?is_public=false`),
  
  getAllProducts: () =>
    apiClient.get('/admin/admin/products'),
  
  updateProductPriority: (productId: number, data: {
    priority_rank?: 'premium' | 'general' | 'basic';
    priority_score?: number;
    is_premium?: boolean;
  }) =>
    apiClient.patch(`/admin/admin/products/${productId}/priority`, data),
  
  approveProduct: (id: number) =>
    apiClient.post(`/admin/admin/products/${id}/approve`),
  
  rejectProduct: (id: number, reason: string) =>
    apiClient.post(`/admin/admin/products/${id}/reject`, { reason }),
  
  getPendingReviews: () =>
    apiClient.get('/admin/reviews/pending'),
  
  approveReview: (id: number) =>
    apiClient.post(`/admin/reviews/${id}/approve`),
  
  rejectReview: (id: number, reason: string) =>
    apiClient.post(`/admin/reviews/${id}/reject`, { reason }),
  
  getAllOrders: () =>
    apiClient.get('/orders/admin/all'),
  
  updateOrderStatus: (id: number, data: { new_status: string }) =>
    apiClient.post(`/orders/admin/${id}/update-status?new_status=${data.new_status}`),
  
  getSalesAnalytics: (startDate?: string, endDate?: string) =>
    apiClient.get(`/admin/analytics/sales?start_date=${startDate}&end_date=${endDate}`),
  
  // Business Settings
  getBusinessSettings: () =>
    apiClient.get('/admin/business-settings'),
  
  saveBusinessSettings: (settings: {
    commission_rate: number;
    delivery_charge: number;
    minimum_commission: number;
    maximum_commission: number;
    currency?: string;
    platform_fee_percentage?: number;
  }) =>
    apiClient.post('/admin/business-settings', null, {
      params: {
        commission_rate: settings.commission_rate.toString(),
        delivery_charge: settings.delivery_charge.toString(),
        minimum_commission: settings.minimum_commission.toString(),
        maximum_commission: settings.maximum_commission.toString(),
      }
    }),
};

// Business Models API
export const businessModelsApi = {
  getAll: (skip = 0, limit = 100) =>
    apiClient.get(`/business-models/business-models?skip=${skip}&limit=${limit}`),
  
  getById: (id: number) =>
    apiClient.get(`/business-models/business-models/${id}`),
  
  create: (data: any) =>
    apiClient.post('/business-models/business-models', data),
  
  update: (id: number, data: any) =>
    apiClient.put(`/business-models/business-models/${id}`, data),
  
  delete: (id: number) =>
    apiClient.delete(`/business-models/business-models/${id}`),
  
  setDefault: (id: number) =>
    apiClient.post(`/business-models/business-models/${id}/set-default`),
  
  // Commission Rules
  getCommissionRules: (businessModelId?: number) => {
    const params = businessModelId ? `?business_model_id=${businessModelId}` : '';
    return apiClient.get(`/business-models/commission-rules${params}`);
  },
  
  createCommissionRule: (data: any) =>
    apiClient.post('/business-models/commission-rules', data),
  
  updateCommissionRule: (id: number, data: any) =>
    apiClient.put(`/business-models/commission-rules/${id}`, data),
  
  deleteCommissionRule: (id: number) =>
    apiClient.delete(`/business-models/commission-rules/${id}`),
  
  // Payment Methods
  getPaymentMethods: () =>
    apiClient.get('/business-models/payment-methods'),
  
  createPaymentMethod: (data: any) =>
    apiClient.post('/business-models/payment-methods', data),
  
  updatePaymentMethod: (id: number, data: any) =>
    apiClient.put(`/business-models/payment-methods/${id}`, data),
  
  // Delivery Methods
  getDeliveryMethods: () =>
    apiClient.get('/business-models/delivery-methods'),
  
  createDeliveryMethod: (data: any) =>
    apiClient.post('/business-models/delivery-methods', data),
  
  updateDeliveryMethod: (id: number, data: any) =>
    apiClient.put(`/business-models/delivery-methods/${id}`, data),
  
  // Shop Types
  getShopTypes: () =>
    apiClient.get('/business-models/shop-types'),
  
  createShopType: (data: any) =>
    apiClient.post('/business-models/shop-types', data),
  
  updateShopType: (id: number, data: any) =>
    apiClient.put(`/business-models/shop-types/${id}`, data),
  
  // Category-Business Model Links
  getCategoryBusinessModels: (categoryId: number) =>
    apiClient.get(`/business-models/categories/${categoryId}/business-models`),
  
  linkCategoryBusinessModel: (categoryId: number, data: any) =>
    apiClient.post(`/business-models/categories/${categoryId}/business-models`, data),
  
  updateCategoryBusinessModel: (linkId: number, data: any) =>
    apiClient.put(`/business-models/category-business-models/${linkId}`, data),
  
  unlinkCategoryBusinessModel: (linkId: number) =>
    apiClient.delete(`/business-models/category-business-models/${linkId}`),
  
  getDefaultBusinessModelForCategory: (categoryId: number) =>
    apiClient.get(`/business-models/categories/${categoryId}/default-business-model`),
  
  // Calculate Commission
  calculateCommission: (data: any) =>
    apiClient.post('/business-models/calculate-commission', data),
  
  // Initialize Defaults
  initializeDefaults: () =>
    apiClient.post('/business-models/initialize-defaults'),
};

// Locations API
export const locationsApi = {
  getAll: (locationType?: string, parentId?: number) => {
    const params: any = {};
    if (locationType) params.location_type = locationType;
    if (parentId !== undefined) params.parent_id = parentId;
    return apiClient.get('/locations/', { params });
  },

  search: (query: string, locationType?: string, limit?: number) => {
    const params: any = { query };
    if (locationType) params.location_type = locationType;
    if (limit) params.limit = limit;
    return apiClient.get('/locations/search', { params });
  },

  getById: (id: number) =>
    apiClient.get(`/locations/${id}`),

  getHierarchy: (countryCode: string) =>
    apiClient.get(`/locations/hierarchy/${countryCode}`),

  getChildren: (parentId: number) =>
    apiClient.get(`/locations/children/${parentId}`),

  create: (locationData: any) =>
    apiClient.post('/locations/', locationData),

  update: (id: number, locationData: any) =>
    apiClient.put(`/locations/${id}`, locationData),

  delete: (id: number) =>
    apiClient.delete(`/locations/${id}`),
};

// Export delivery services
export { deliveryOptionApi } from './deliveryOptionService';
export { shopDeliveryOfferApi } from './shopDeliveryOfferService';

export default api;
